import { useDescope } from '@descope/react-sdk';
import { useGetAssumedUserQuery, useGetCurrentUserQuery } from '@src/apiFetch/authApi';
import { sendLoginFingerprint } from '@src/apiFetch/loginEventsApi';
import useSessionManager from '@src/hooks/useSessionManager';
import { useCallback, useContext, useEffect } from 'react';
import { adminModeState } from '@src/store';
import { BroadcastChannelContext } from '../context/BroadcastChannelContext';
import { stopUnthreadChat } from './useUnthreadChat';
import usePostHogIdentify from '@src/hooks/usePostHogIdentify';
import { trackEvent } from '@src/utils/trackEvent';

const useUserSession = () => {
    const { logout: logoutDescope, isSessionLoading, sessionToken } = useSessionManager();
    const { getSessionToken } = useDescope();
    const bc = useContext(BroadcastChannelContext);

    // Load /me before anything else to prevent multiple endpoints from trying to provision the user on-demand
    const authUserQuery = useGetCurrentUserQuery({
        enabled: !isSessionLoading && !!sessionToken,
    });
    usePostHogIdentify(authUserQuery.data);
    const assumedUserId = adminModeState((state) => state.id);
    const assumedUserQuery = useGetAssumedUserQuery({ userId: assumedUserId }, { enabled: !!assumedUserId });

    // Send fingerprint data after successful auth (/me returns user data)
    useEffect(() => {
        if (!authUserQuery.data) return;

        const stored = sessionStorage.getItem('login_fingerprint');
        if (!stored) return;

        sessionStorage.removeItem('login_fingerprint');

        try {
            const fp = JSON.parse(stored);
            sendLoginFingerprint(fp.visitorId, fp);
        } catch (err) {
            // Ignore malformed data
        }
    }, [authUserQuery.data]);

    useEffect(() => {
        if (!authUserQuery.data) return;

        if (!sessionStorage.getItem('ph_login_tracked')) {
            sessionStorage.setItem('ph_login_tracked', '1');
            trackEvent('login_succeeded', {
                auth_method: 'descope',
            });
        }
    }, [authUserQuery.data]);

    const logout = () => {
        sessionStorage.removeItem('adminMode');
        sessionStorage.removeItem('ph_login_tracked');
        logoutDescope();
        bc.postMessage({ type: 'logout' });
        setTimeout(() => {
            localStorage.setItem('bannerDismissed', 'false');
            stopUnthreadChat();
        }, 500);
    };

    const refetch = useCallback(() => {
        authUserQuery.refetch();
        if (assumedUserId) {
            assumedUserQuery.refetch();
        }
    }, [authUserQuery.refetch, assumedUserQuery.refetch]);

    const isRetrievingUser = isSessionLoading || authUserQuery.isLoading || assumedUserQuery.isLoading;
    const currentUser = assumedUserId ? assumedUserQuery.data : authUserQuery.data;
    return {
        isRetrievingUser,
        logout,
        accessToken: sessionToken,
        getAccessToken: getSessionToken,
        authUser: authUserQuery.data,
        user: isRetrievingUser ? null : currentUser,
        refetch,
    };
};

export default useUserSession;
